import React, { lazy, Suspense, useState, useEffect } from 'react';
import { Container, Title, Tabs, Box, Text, ThemeIcon, rem, Button, Group, Loader, Stack, Burger, Drawer, NavLink, Modal, Paper } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconWand, IconSettings, IconUser, IconInfoCircle, IconHistory, IconShield, IconAlertTriangle, IconClipboard } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { ThemeToggle } from './components/ThemeToggle';
import { LanguageSelector } from './components/LanguageSelector';
import { AIModelSelector } from './components/AIModelSelector';
// Conditional import for Tauri event listener
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import './styles.css';

import { apiService } from './services/apiService'; // Import without User type
import { BASE_URL } from './config/apiConfig';
import { invoke, Channel } from '@tauri-apps/api/core';
import { listen } from '@tauri-apps/api/event';
import { Settings } from './types';
import { getUserPlanFromBackend } from './services/planService';

// Import User type separately to avoid conflicts
import  type { User } from './services/apiService';
import { isWeb, isMobile, isAndroid } from "./utils/environment";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { aiService } from "./services/aiService.ts";
import { HotkeyService } from './services/hotkeyService';
import { restoreFieldButtonSetting } from './services/grammarService';
import { useTranslation } from 'react-i18next';

import { getAppVersion } from './utils/version';
import { NotificationBell } from './components/NotificationBell';

// Simple theme configuration
// const theme = {
//   primaryColor: 'blue',
//   fontFamily: 'Segoe UI, sans-serif',
// };

const TextCorrection = lazy(() => import('./components/TextCorrection').then(m => ({ default: m.TextCorrection })));
const SettingsTabs = lazy(() => import('./components/settings/SettingsTabs'));
const AccountSettings = lazy(() => import('./components/settings/AccountSettings').then(m => ({ default: m.AccountSettings })));
const About = lazy(() => import('./components/About').then(m => ({ default: m.About })));
const Docs = lazy(() => import('./components/Docs').then(m => ({ default: m.Docs })));
const Privacy = lazy(() => import('./components/Privacy').then(m => ({ default: m.Privacy })));
const LoginPage = lazy(() => import('./components/LoginPage').then(m => ({ default: m.LoginPage })));
const ForgotPassword = lazy(() => import('./components/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import('./components/ResetPassword').then(m => ({ default: m.ResetPassword })));
const ActivateAccount = lazy(() => import('./components/ActivateAccount').then(m => ({ default: m.ActivateAccount })));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const HomePage = lazy(() => import('./components/HomePage').then(m => ({ default: m.HomePage })));
const Terms = lazy(() => import('./components/Terms').then(m => ({ default: m.Terms })));
const Blog = lazy(() => import('./components/Blog').then(m => ({ default: m.Blog })));
const BlogPostPage = lazy(() => import('./components/BlogPostPage').then(m => ({ default: m.BlogPostPage })));
const SeoPageWrapper = lazy(() => import('./components/SeoPageWrapper').then(m => ({ default: m.SeoPageWrapper })));
const History = lazy(() => import('./components/History').then(m => ({ default: m.History })));
const Onboarding = lazy(() => import('./components/Onboarding').then(m => ({ default: m.Onboarding })));
const AuthCallback = lazy(() => import('./components/AuthCallback').then(m => ({ default: m.AuthCallback })));
const MobileAccessibilityButton = lazy(() => import('./components/MobileAccessibilityButton'));
const AndroidAccessibilityOnboarding = lazy(() => import('./components/AndroidAccessibilityOnboarding'));
const ClipboardHistory = lazy(() => import('./components/ClipboardHistory').then(m => ({ default: m.ClipboardHistory })));
const FloatingAIAssistantPage = lazy(() => import('./components/SeoPages').then(m => ({ default: m.FloatingAIAssistantPage })));

/**
 * Persists a block/allow decision made from a native quick-menu (desktop tray.rs's
 * "persist_app_access" emit, or Android FloatingOverlay.kt's quick menu via the
 * mobile_watch_app_access channel) to the backend settings, keeping both native sides
 * consistent with the saved allowedApps list. Shared by both platforms' listeners below
 * since the persistence logic itself - and the DB shape - is identical.
 */
async function persistAppAccessChange(appName: string, enabled: boolean) {
    try {
        // Ensure API token is available for background tray/menu actions.
        // This can run before the initial login/bootstrap effect finishes.
        const token = localStorage.getItem('token');
        if (token) {
            apiService.setToken(token);
        }

        console.log('[app-access] persist_app_access received', { appName, enabled });

        const response = await apiService.getSettings();
        const currentSettings = response.settings || {};
        const currentAllowedApps = (currentSettings.allowedApps || []) as any[];

        const needle = appName.toLowerCase();
        const idx = currentAllowedApps.findIndex((a) => {
            const name = typeof a?.name === 'string' ? a.name.toLowerCase() : '';
            const processName = typeof a?.processName === 'string' ? a.processName.toLowerCase() : '';
            const bundleId = typeof a?.bundleId === 'string' ? a.bundleId.toLowerCase() : '';

            return (
                (name && (name === needle || name.includes(needle) || needle.includes(name))) ||
                (processName &&
                    (processName === needle ||
                        processName.includes(needle) ||
                        needle.includes(processName))) ||
                (bundleId && bundleId === needle)
            );
        });

        let updatedAllowedApps = [...currentAllowedApps];
        if (idx >= 0) {
            updatedAllowedApps[idx] = {
                ...updatedAllowedApps[idx],
                enabled,
            };
        } else {
            updatedAllowedApps.push({
                id: `tray-${Date.now()}`,
                name: appName,
                path: '',
                bundleId: null,
                processName: null,
                enabled,
            });
        }

        await apiService.updateSettings({
            ...currentSettings,
            allowedApps: updatedAllowedApps,
        });

        console.log('[app-access] persist_app_access saved to DB', { appName, enabled });

        // Keep desktop's Rust-side enforcement cache consistent immediately (set_allowed_apps/
        // set_blocked_apps are desktop-only commands - main.rs - not registered on Android,
        // where AppAccessRules.kt is already updated natively before this even runs).
        if (!isAndroid()) {
            const allowedApps = updatedAllowedApps.filter((a) => a.enabled !== false);
            const blockedApps = updatedAllowedApps.filter((a) => a.enabled === false);
            await invoke('set_allowed_apps', { apps: allowedApps });
            await invoke('set_blocked_apps', { apps: blockedApps });
        }
    } catch (err) {
        console.error('[app-access] Failed to persist app access change:', err);
    }
}
const GrammarCheckerPage = lazy(() => import('./components/SeoPages').then(m => ({ default: m.GrammarCheckerPage })));
const TranslationAssistantPage = lazy(() => import('./components/SeoPages').then(m => ({ default: m.TranslationAssistantPage })));
const DownloadPage = lazy(() => import('./components/SeoPages').then(m => ({ default: m.DownloadPage })));


function AppContent() {

    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    // Push localized tray-menu labels to the backend. The locale JSON files are the single source
    // of truth; the native tray (Rust) has no i18n of its own, so we send it the translated strings
    // on startup and whenever the language changes.
    useEffect(() => {
        if (isWeb()) return;
        const trayKeys = [
            'showApp', 'generalSettings', 'allowedAppsSettings', 'allowThisApp', 'dontAllowThisApp',
            'pause1h', 'pause2h', 'openAccessibility', 'monitoringOn', 'monitoringOff', 'monitoringSignIn',
            'clipboardHistory', 'clearHistory', 'smartClipboardOn', 'smartClipboardOff', 'restart', 'quit',
            'statusAllowed', 'statusBlocked', 'currentApp', 'selectedApp', 'currentAppUnknown', 'appStatus',
            'pausedHourMin', 'pausedHour', 'pausedMin', 'pausedSec',
        ];
        const labels: Record<string, string> = {};
        trayKeys.forEach((k) => { labels[`tray.${k}`] = t(`tray.${k}`); });
        invoke('set_tray_labels', { labels }).catch((e) => console.error('Failed to set tray labels:', e));
    }, [t, i18n.language]);

    // Same idea as the tray-labels push above, for Android's quick menu (FloatingOverlay.kt's
    // showQuickMenuInternal - also native with no i18n of its own). Reuses the exact same
    // tray.* keys rather than a second, Android-only copy of the same strings.
    useEffect(() => {
        if (!isAndroid()) return;
        const menuKeys = [
            'pause1h', 'pause2h', 'showApp', 'openAccessibility',
            'allowThisApp', 'dontAllowThisApp', 'statusAllowed', 'statusBlocked', 'currentApp', 'appStatus',
            // Shown after dragging the floating button onto the hide target (DragHideUi.kt).
            'buttonHidden',
        ];
        const labels: Record<string, string> = {};
        menuKeys.forEach((k) => { labels[`tray.${k}`] = t(`tray.${k}`); });
        invoke('mobile_set_menu_labels', { labels }).catch((e) => console.error('Failed to set Android menu labels:', e));
    }, [t, i18n.language]);

    // Desktop forgets the writing-button switch on every launch (it lives in a Rust atomic),
    // so the stored answer is pushed back here - the same startup-restore the clipboard
    // hotkey does. A no-op on Android, which keeps its own copy.
    useEffect(() => {
        restoreFieldButtonSetting().catch(() => undefined);
    }, []);

    // The gear beside the floating bubble asks for a screen while this app is in the
    // background - often with no webview running at all - so it leaves the request in
    // SharedPreferences instead of firing an event into the void (see NavigationRequest.kt).
    // Collected here on launch and every time the app comes back to the foreground.
    useEffect(() => {
        if (!isAndroid()) return;

        const collect = () => {
            invoke<string | null>('mobile_take_pending_navigation')
                .then((target) => {
                    if (target !== 'allowed-apps') return;
                    handleTabChange('settings');
                    localStorage.setItem('activeSettingsTab', 'allowed-apps');
                    navigate('/settings/allowed-apps', { replace: true });
                })
                .catch((e) => console.error('Failed to read pending navigation:', e));
        };

        collect();
        const onVisible = () => {
            if (document.visibilityState === 'visible') collect();
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, []);

    // Android's quick menu (FloatingOverlay.kt) is a plain WebView with no Tauri runtime,
    // so it can't emit "persist_app_access" itself like desktop's tray.rs does - instead it
    // pushes through this channel (mobile_watch_app_access -> KalimatPlugin.notifyAppAccessChanged),
    // which persists via the exact same shared logic as the desktop listener above.
    useEffect(() => {
        if (!isAndroid()) return;

        const channel = new Channel<{ appName?: string; enabled?: boolean }>();
        channel.onmessage = (payload) => {
            if (!payload?.appName || typeof payload.enabled !== 'boolean') return;
            persistAppAccessChange(payload.appName, payload.enabled);
        };

        invoke('mobile_watch_app_access', { channel }).catch((e) =>
            console.error('Failed to start watching app access changes:', e)
        );
    }, []);

    // Pin to taskbar on first launch (Windows only)
    useEffect(() => {
        if (isWeb()) return;
        const hasPinned = localStorage.getItem('has_pinned_to_taskbar');
        if (!hasPinned) {
            invoke('pin_to_taskbar')
                .then(() => {
                    localStorage.setItem('has_pinned_to_taskbar', 'true');
                    console.log('Successfully pinned to taskbar');
                })
                .catch((e) => console.error('Failed to pin to taskbar:', e));
        }
    }, []);

    // Get tab from path or localStorage or default to 'home'
    const getInitialTab = () => {
        const path = location.pathname.split('/')[1];
        if (path) return path;

        return localStorage.getItem('activeTab') || 'home';
    };

    // Set default tab based on path/localStorage or 'home'
    const [activeTab, setActiveTab] = useState<string | null>(getInitialTab());

    // Handle tab change
    const handleTabChange = (value: string | null) => {
        if (!value) return;

        setActiveTab(value);
        // Persist to localStorage for desktop app refresh
        localStorage.setItem('activeTab', value);

        // Sync with path for web
        // For admin and settings, we might want to restore their previous subtabs
        if (value === 'admin') {
            const subtab = localStorage.getItem('activeAdminTab') || 'users';
            navigate(`/admin/${subtab}`, { replace: true });
        } else if (value === 'settings') {
            const subtab = localStorage.getItem('activeSettingsTab') || 'general';
            navigate(`/settings/${subtab}`, { replace: true });
        } else {
            navigate(`/${value}`, { replace: true });
        }
    };
    const [user, setUser] = useState<User | null>(null);
    const [settings, setSettings] = useState<Settings | null>(null);
    const [callsRemaining, setCallsRemaining] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true); // Add loading state
    const [appVersion, setAppVersion] = useState<string>('1.0.0');
    
    // Version check state
    const [versionCheck, setVersionCheck] = useState<{
        supported: boolean;
        requiresUpdate: boolean;
        forceUpdate: boolean;
        latestVersion: string;
        updateUrl: string;
        message: string;
    } | null>(null);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    
    // Onboarding state - will be updated after version loads
    const [showOnboarding, setShowOnboarding] = useState(false);

    const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] = useDisclosure(false);

    // Check if user is already logged in
    useEffect(() => {
        let unlistenFn: (() => void) | undefined;
        let unlistenPersistAppAccessFn: (() => void) | undefined;

        if (!isWeb()) {
            const unlistenPromise = listen<{ tab?: string; settingsTab?: string; adminTab?: string }>('navigate', (event) => {
                if (event.payload?.tab) {
                    handleTabChange(event.payload.tab);
                }
                if (event.payload?.settingsTab) {
                    localStorage.setItem('activeSettingsTab', event.payload.settingsTab);
                    navigate(`/settings/${event.payload.settingsTab}`, { replace: true });
                }
                if (event.payload?.adminTab) {
                    localStorage.setItem('activeAdminTab', event.payload.adminTab);
                    navigate(`/admin/${event.payload.adminTab}`, { replace: true });
                }
            });

            const unlistenPersistAppAccessPromise = listen<{ appName?: string; enabled?: boolean }>(
                'persist_app_access',
                (event) => {
                    const appName = event.payload?.appName;
                    const enabled = event.payload?.enabled;
                    if (!appName || typeof enabled !== 'boolean') {
                        return;
                    }
                    persistAppAccessChange(appName, enabled);
                },
            );

            unlistenPromise.then((unlisten) => { unlistenFn = unlisten; }).catch(() => undefined);
            unlistenPersistAppAccessPromise.then((unlisten) => { unlistenPersistAppAccessFn = unlisten; }).catch(() => undefined);
        }

        const initAuth = async () => {
            let storedToken = localStorage.getItem('token');
            if (!storedToken && !isWeb()) {
                try {
                    const saved = await invoke<string | null>('get_saved_token');
                    if (saved) {
                        localStorage.setItem('token', saved);
                        storedToken = saved;
                    }
                } catch (err) {
                    console.error('Failed to load saved token:', err);
                }
            }

            if (storedToken) {
                apiService.setToken(storedToken);
                window.dispatchEvent(new CustomEvent('ai-config-changed'));
                if (!isWeb()) {
                    import('@tauri-apps/api/event')
                        .then(({ emit }) => emit('ai-config-changed'))
                        .catch(() => undefined);
                }
                // Fetch user profile
                apiService.getProfile()
                    .then(response => {

                        // Set default plan to 'free' if not defined
                        const userWithDefaultPlan = {
                            ...response.user,
                            plan: response.user.plan || 'free'
                        };
                        setUser(userWithDefaultPlan);

                        // Check if onboarding should be shown for this version
                        getAppVersion().then((version) => {
                            setAppVersion(version);
                            const lastSeenVersion = localStorage.getItem('lastSeenOnboardingVersion');
                            if (lastSeenVersion !== version) {
                                setShowOnboarding(true);
                            }
                        });

                        // Initialize AI service with backend settings
                        aiService.initializeWithAISettingsFromBackend().catch(err => {
                            console.error('Failed to initialize AI service:', err);
                        });

                        // Initialize hotkey service
                        HotkeyService.getInstance().initialize().then(() => {
                            // Register the toggle app visibility hotkey
                            const hotkeyService = HotkeyService.getInstance();
                            hotkeyService.registerHotkey('toggleAppVisibility', () => {
                                // This is now handled in the backend, but we keep this for completeness
                                console.log('Toggle app visibility hotkey triggered');
                            });
                            // Register the activate text selection hotkey to toggle monitoring
                            hotkeyService.registerHotkey('activateTextSelection', () => {
                                // This is now handled in the backend
                                console.log('Activate text selection hotkey triggered');
                            });
                            // Register the clipboard history hotkey from saved settings so it
                            // survives restarts (the backend otherwise falls back to its default).
                            // HotkeyService doesn't track this action, so register it directly.
                            apiService.getSettings().then((res) => {
                                const clipboardHotkey = res.settings?.hotkeys?.show_clipboard_history || 'Ctrl+Shift+V';
                                invoke('register_global_hotkey', {
                                    hotkey: clipboardHotkey,
                                    action: 'show_clipboard_history',
                                }).catch((e) => console.error('Failed to register clipboard hotkey:', e));
                            }).catch((e) => console.error('Failed to load settings for clipboard hotkey:', e));
                        }).catch(err => {
                            console.error('Failed to initialize hotkey service:', err);
                        });

                        // Persist token to backend so other windows (popup) and restarts stay authenticated
                        if (!isWeb()) {
                            invoke('save_token', { token: storedToken })
                                .catch(err => console.error('Failed to save token:', err));

                            // Notify backend that user is authenticated
                            invoke('set_authentication_status', { status: true })
                                .catch(err => console.error('Failed to set authentication status:', err));

                            // Fetch plan limits and update backend
                            apiService.getImagePlanLimits()
                                .then(limits => {
                                    const enabled = !!limits?.enabled && limits.maxImagesPerDay > 0;
                                    invoke('set_tool_button_plan_enabled', { enabled })
                                        .catch(err => console.error('Failed to set plan enabled state:', err));
                                })
                                .catch(err => {
                                    console.error('Failed to fetch image plan limits:', err);
                                    invoke('set_tool_button_plan_enabled', { enabled: false })
                                        .catch(() => {});
                                });
                        }
                    })
                    .catch(err => {
                        console.error('Failed to fetch user profile:', err);
                        // Token might be invalid, remove it
                        localStorage.removeItem('token');
                        if (!isWeb()) {
                            invoke('clear_saved_token')
                                .catch(e => console.error('Failed to clear saved token:', e));
                            // Notify backend that user is not authenticated
                            invoke('set_authentication_status', { status: false })
                                .catch(err => console.error('Failed to set authentication status:', err));
                            invoke('set_tool_button_plan_enabled', { enabled: false })
                                .catch(() => {});
                        }
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
            } else {
                // Notify backend that user is not authenticated
                if (!isWeb()) {
                    invoke('set_authentication_status', { status: false })
                        .catch(err => console.error('Failed to set authentication status:', err));
                    invoke('set_tool_button_plan_enabled', { enabled: false })
                        .catch(() => {});
                }
                setIsLoading(false);
            }
        };

        initAuth();

        // Check app version on startup
        checkAppVersion();

        // Listen for custom navigation events
        const handleNavigateToDocs = () => {
            handleTabChange('docs');
        };
        
        const handleNavigateToPrivacy = () => {
            handleTabChange('privacy');
        };

        const handleNavigateToTerms = () => {
            handleTabChange('terms');
        };

        const handleNavigateToDownload = () => {
            handleTabChange('download');
        };

        const handleNavigate = (e: Event) => {
            const ce = e as CustomEvent;
            const tab = (ce?.detail as any)?.tab;
            if (typeof tab === 'string' && tab.length > 0) {
                handleTabChange(tab);
            }
        };
        
        window.addEventListener('navigate-to-docs', handleNavigateToDocs);
        window.addEventListener('navigate-to-privacy', handleNavigateToPrivacy);
        window.addEventListener('navigate-to-terms', handleNavigateToTerms);
        window.addEventListener('navigate-to-download', handleNavigateToDownload);
        window.addEventListener('navigate', handleNavigate as EventListener);

        return () => {
            if (unlistenFn) unlistenFn();
            if (unlistenPersistAppAccessFn) unlistenPersistAppAccessFn();
            window.removeEventListener('navigate-to-docs', handleNavigateToDocs);
            window.removeEventListener('navigate-to-privacy', handleNavigateToPrivacy);
            window.removeEventListener('navigate-to-terms', handleNavigateToTerms);
            window.removeEventListener('navigate-to-download', handleNavigateToDownload);
            window.removeEventListener('navigate', handleNavigate as EventListener);
        };
    }, []);

    // Update calls remaining when user changes
    useEffect(() => {
        if (user) {
            updateCallsRemaining();
        }
    }, [user]);

    const updateCallsRemaining = async () => {
        try {
            const userPlan = await getUserPlanFromBackend();
            const remaining = userPlan.maxCallsPerDay - userPlan.usage;
            setCallsRemaining(`${remaining} calls remaining`);
        } catch (error) {
            console.error('Error getting user plan:', error);
            setCallsRemaining('Error loading');
        }
    };

    // Fetches the admin "Apps" tab's platform-wide config (blocked_default etc.) from the
    // server and pushes it into the native enforcement cache (Rust's app_positions on desktop,
    // AdminAppPositions.kt on Android via mobile_set_app_positions). Shared by: the on-login
    // sync, the admin's own immediate-refresh listener, and the periodic poll below - all three
    // need to build/send the exact same payload, so this used to be copy-pasted three times.
    const syncAppPositionsToNative = async () => {
        if (isWeb()) return;
        try {
            const res = await apiService.getAppPositions();
            const positions = (res.appPositions || []).map((p: any) => ({
                name: p.name,
                bundle_id: p.bundle_id,
                process_name: p.process_name,
                platform: p.platform,
                float_dx: Number(p.float_dx) || 0,
                float_dy: Number(p.float_dy) || 0,
                popup_dx: Number(p.popup_dx) || 0,
                popup_dy: Number(p.popup_dy) || 0,
                blocked_default: !!p.blocked_default,
            }));
            // 'set_app_positions' is desktop-only (main.rs); Android's native
            // detection (SelectionAccessibilityService.kt) needs the
            // Android-specific command instead - see AdminAppPositions.tsx's
            // pushPositionsToTauri for the same branch.
            if (isAndroid()) {
                await invoke('mobile_set_app_positions', { positions });
            } else {
                await invoke('set_app_positions', { positions });
            }
        } catch (e) {
            console.error('Failed to sync app positions to Tauri', e);
        }
    };

    // Load settings from backend when user logs in
    useEffect(() => {
        if (user) {
            loadSettings();
            // Also load global app positions and push to Tauri
            syncAppPositionsToNative();
        }
    }, [user]);

    // Added a separate effect to sync app positions specifically when an admin
    // saves them in the "Apps" tab, since the effect above only runs once on login.
    // This only ever fires on the admin's own device: 'admin-app-positions-updated' is a
    // local Tauri event (@tauri-apps/api/event emit/listen), scoped to the single app
    // process that called emit() - it is not a network broadcast, so it can never reach any
    // other user's device.
    useEffect(() => {
        if (!user?.is_admin || isWeb()) return;

        const unlistenPromise = listen('admin-app-positions-updated', () => {
            syncAppPositionsToNative();
        });

        return () => {
            unlistenPromise.then(u => u());
        };
    }, [user]);

    // Periodic poll so a "block for everyone" saved in the admin Apps tab actually reaches
    // everyone, not just the admin's own device. Without this, a regular user's session -
    // once already logged in and running - had no way to learn about the change short of
    // fully restarting the app, since the effect above only notifies the admin's own process
    // (see its comment) and the on-login effect only runs once per login. This affects every
    // platform, but matters most on Android/desktop where the app is meant to keep running
    // in the background for long stretches without a restart.
    useEffect(() => {
        if (!user || isWeb()) return;
        const intervalId = setInterval(() => {
            syncAppPositionsToNative();
        }, 5 * 60 * 1000); // 5 minutes - frequent enough that a block takes effect promptly
                            // without a restart, infrequent enough to be a negligible request cost
        return () => clearInterval(intervalId);
    }, [user]);

    const loadSettings = () => {
        // Load settings
        apiService.getSettings()
            .then(response => {
                const loadedSettings = response.settings || {};
                setSettings(loadedSettings);

                if (!isWeb()) {
                    invoke('set_clipboard_monitoring_enabled', {
                        enabled: loadedSettings.clipboardMonitoring !== false,
                    }).catch(err => console.error('Failed to sync clipboard monitoring setting:', err));
                }
            })
            .catch(err => {
                console.error('Failed to fetch settings:', err);
            });
    };

    // Function to add item to history (passed to TextCorrection)
    const addToHistory = (text: string, action: 'correct' | 'translate' | 'ask' | 'manual', result: string) => {
        // Save to backend if user is logged in and history is enabled
        if (user && (!settings || settings.historyEnabled !== false)) {
            apiService.addToHistory(text, result, action)
                .catch((err: any) => {
                    console.error('Failed to save to backend:', err);
                });
        }

        // Update calls remaining immediately after AI actions (not manual entries)
        if (action !== 'manual') {
            updateCallsRemaining();
        }
    };

    // Check app version on startup
    const checkAppVersion = async () => {
        try {
            // Determine platform
            let platform = 'web';
            if (!isWeb()) {
                // Check if mobile or desktop
                const userAgent = navigator.userAgent.toLowerCase();
                platform = /android|iphone|ipad|ipod/.test(userAgent) ? 'mobile' : 'desktop';
            }

            console.log('[Version] Checking version for platform:', platform);

            // Get current app version dynamically
            const currentVersion = await getAppVersion();
            console.log('[Version] Current app version:', currentVersion);

            const response = await fetch(`${BASE_URL}/api/version/check?platform=${platform}&version=${currentVersion}`);
            
            if (!response.ok) {
                console.warn('[Version] Failed to check app version, status:', response.status);
                return;
            }

            const versionData = await response.json();
            console.log('[Version] Version check result:', versionData);
            setVersionCheck(versionData);

            // Show modal if update is required
            if (versionData.requiresUpdate) {
                console.log('[Version] Update required, showing modal');
                setShowUpdateModal(true);
            } else {
                console.log('[Version] Version is up to date');
            }
        } catch (error) {
            console.error('[Version] Error checking app version:', error);
        }
    };

    // Handle login
    const handleLogin = async (userData: User) => {

        // Set default plan to 'free' if not defined
        const userWithDefaultPlan = {
            ...userData,
            plan: userData.plan || 'free'
        };
        setUser(userWithDefaultPlan);
        handleTabChange('text-correction');

        window.dispatchEvent(new CustomEvent('ai-config-changed'));
        if (!isWeb()) {
            import('@tauri-apps/api/event')
                .then(({ emit }) => emit('ai-config-changed'))
                .catch(() => undefined);
        }

        // Initialize AI service with backend settings after login
        try {
            await aiService.initializeWithAISettingsFromBackend();
            console.log('AI service initialized successfully after login');
        } catch (err) {
            console.error('Failed to initialize AI service after login:', err);
            notifications.show({
                title: 'Warning',
                message: 'AI service initialization failed. You may need to refresh the page.',
                color: 'orange',
            });
        }

        // Initialize hotkey service after login
        try {
            await HotkeyService.getInstance().initialize();
            // Register the toggle app visibility hotkey
            const hotkeyService = HotkeyService.getInstance();
            hotkeyService.registerHotkey('toggleAppVisibility', () => {
                // This is now handled in the backend, but we keep this for completeness
                console.log('Toggle app visibility hotkey triggered');
            });
            // Register the activate text selection hotkey to toggle monitoring
            hotkeyService.registerHotkey('activateTextSelection', () => {
                // This is now handled in the backend
                console.log('Activate text selection hotkey triggered');
            });
            console.log('Hotkey service initialized successfully after login');
        } catch (err) {
            console.error('Failed to initialize hotkey service after login:', err);
        }

        // Notify backend that user is authenticated
        if (!isWeb()) {
            invoke('set_authentication_status', { status: true })
                .catch(err => console.error('Failed to set authentication status:', err));

            const token = localStorage.getItem('token');
            if (token) {
                invoke('save_token', { token })
                    .catch(err => console.error('Failed to save token:', err));
            }

            // Fetch plan limits and update backend after login
            apiService.getImagePlanLimits()
                .then(limits => {
                    const enabled = !!limits?.enabled && limits.maxImagesPerDay > 0;
                    invoke('set_tool_button_plan_enabled', { enabled })
                        .catch(err => console.error('Failed to set plan enabled state:', err));
                })
                .catch(err => {
                    console.error('Failed to fetch image plan limits after login:', err);
                    invoke('set_tool_button_plan_enabled', { enabled: false })
                        .catch(() => {});
                });
        }
    };

    // Handle logout
    const handleLogout = () => {

        setUser(null);
        localStorage.removeItem('token');
        apiService.setToken('');
        handleTabChange('text-correction');

        // Notify backend that user is no longer authenticated
        if (!isWeb()) {
            invoke('set_authentication_status', { status: false })
                .catch(err => console.error('Failed to set authentication status:', err));

            invoke('clear_saved_token')
                .catch(err => console.error('Failed to clear saved token:', err));

            invoke('set_tool_button_plan_enabled', { enabled: false })
                .catch(() => {});
        }
    };

    // Function to update settings
    const updateSettings = (newSettings: Partial<Settings>) => {
        const updatedSettings = { ...settings, ...newSettings } as Settings;
        setSettings(updatedSettings);

        if (!isWeb() && newSettings.hasOwnProperty('clipboardMonitoring')) {
            invoke('set_clipboard_monitoring_enabled', {
                enabled: newSettings.clipboardMonitoring !== false,
            }).catch(err => console.error('Failed to sync clipboard monitoring setting:', err));
        }

        // Update settings in backend
        apiService.updateSettings(updatedSettings)
            .then(() => {
                // Reload settings if history setting changed
                if (newSettings.hasOwnProperty('historyEnabled')) {
                    loadSettings();
                }
            })
            .catch(err => {
                console.error('Failed to update settings:', err);
                notifications.show({
                    title: 'Error',
                    message: 'Failed to update settings',
                    color: 'red',
                });
            });
    };

    // Sync activeTab with path changes (e.g., browser back/forward)
    useEffect(() => {
        const path = location.pathname.split('/')[1];
        if (path && path !== activeTab) {
            setActiveTab(path);
            localStorage.setItem('activeTab', path);
        } else if (!path && activeTab !== 'home') {
            setActiveTab('home');
            localStorage.setItem('activeTab', 'home');
        }
    }, [location.pathname]);

    // Update active tab when user state changes
    useEffect(() => {
        // Don't redirect while still checking authentication
        if (isLoading) return;

        if (user) {
            // Only force text-correction if we are on home/login/about/docs
            const publicTabs = ['home', 'login', 'about', 'docs'];
            if (!activeTab || publicTabs.includes(activeTab)) {
                handleTabChange('text-correction');
            }
        } else {
            // If not logged in and on a private tab, go home
            const privateTabs = ['text-correction', 'history', 'account', 'admin', 'settings'];
            if (activeTab && privateTabs.includes(activeTab)) {
                handleTabChange('home');
            }
        }
    }, [user, isLoading]);

    // Listen for theme changes and apply them immediately
    useEffect(() => {
        const handleThemeChanged = (event: CustomEvent<{ theme: string }>) => {
            const { theme } = event.detail || {};
            if (theme && theme !== 'system') {
                // Apply theme immediately using Mantine's color scheme
                document.documentElement.setAttribute('data-mantine-color-scheme', theme);
            } else if (theme === 'system') {
                // Detect system preference and apply it
                const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
                const systemTheme = prefersDark ? 'dark' : 'light';
                document.documentElement.setAttribute('data-mantine-color-scheme', systemTheme);
            }
        };

        window.addEventListener('theme-changed', handleThemeChanged as EventListener);
        return () => {
            window.removeEventListener('theme-changed', handleThemeChanged as EventListener);
        };
    }, []);

    // Show loading state while checking authentication
    if (isLoading) {
        return (
            <Box style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Container size="sm">
                    <Group justify="center" mb="md">
                        <Title order={1} style={{ fontSize: rem(36) }}>
                            Kalimat AI
                        </Title>
                    </Group>
                    <Group justify="center">
                        <Loader size="lg" />
                        <Text>{t('app.loading')}</Text>
                    </Group>
                </Container>
            </Box>
        );
    }

    // If user is not logged in, show login page
    if (!user) {
        // Only show homepage in web environment, not in Tauri app
        if (isWeb()) {
            return (
                <Box py={{ base: 'md', sm: 'xl' }} px={{ base: 'xs', sm: 'md' }}>
                    <Container size="lg">
                        <Box pt={isAndroid() ? 'max(env(safe-area-inset-top, 0px), 32px)' : '0'}>
                            <Group justify="space-between" mb="xl">
                                <Group>
                                    <Burger opened={drawerOpened} onClick={toggleDrawer} hiddenFrom="sm" size="sm" />
                                    <Title order={1} style={{ fontSize: rem(36) }} className="app-title">
                                        Kalimat AI
                                    </Title>
                                </Group>
                                <Group><NotificationBell /><LanguageSelector /><ThemeToggle /></Group>
                            </Group>
                        </Box>

                        <Drawer opened={drawerOpened} onClose={closeDrawer} title="Menu" padding="md" size="xs">
                            <Stack gap="sm">
                                <NavLink label={t('app.tabs.home')} active={activeTab === 'home'} onClick={() => { handleTabChange('home'); closeDrawer(); }} />
                                <NavLink label={t('app.tabs.docs')} active={activeTab === 'docs'} onClick={() => { handleTabChange('docs'); closeDrawer(); }} />
                                <NavLink label={t('app.tabs.login')} active={activeTab === 'login'} onClick={() => { handleTabChange('login'); closeDrawer(); }} />
                                <NavLink label={t('app.tabs.about')} active={activeTab === 'about'} onClick={() => { handleTabChange('about'); closeDrawer(); }} />
                            </Stack>
                        </Drawer>

                        <Tabs value={activeTab} onChange={handleTabChange}>
                            <Tabs.List grow visibleFrom="sm">
                                <Tabs.Tab value="home">{t('app.tabs.home')}</Tabs.Tab>
                                <Tabs.Tab value="docs">{t('app.tabs.docs')}</Tabs.Tab>
                                <Tabs.Tab value="login">{t('app.tabs.login')}</Tabs.Tab>
                                <Tabs.Tab value="about">{t('app.tabs.about')}</Tabs.Tab>
                            </Tabs.List>

                            <Tabs.Panel value="home" pt="md">
                                <HomePage />
                            </Tabs.Panel>

                            <Tabs.Panel value="login" pt="md">
                                <LoginPage onLogin={handleLogin} />
                            </Tabs.Panel>

                            <Tabs.Panel value="docs" pt="md">
                                <Docs />
                            </Tabs.Panel>

                            <Tabs.Panel value="about" pt="md">
                                <About />
                            </Tabs.Panel>
                        </Tabs>
                    </Container>
                </Box>
            );
        } else {
            // In Tauri app, go straight to login
            return (
                <Box py="xl" px="md">
                    <Container size="lg">
                        <Box pt={isAndroid() ? 'max(env(safe-area-inset-top, 0px), 32px)' : '0'}>
                            <Group justify="space-between" mb="xl">
                                <Title order={1} style={{ fontSize: rem(36) }}>
                                    Kalimat AI
                                </Title>
                                <Group><NotificationBell /><LanguageSelector /><ThemeToggle /></Group>
                            </Group>
                        </Box>
                        <LoginPage onLogin={handleLogin} />
                    </Container>
                </Box>
            );
        }
    }


    return (
        <Box py={{ base: 'md', sm: 'xl' }} px={{ base: 'xs', sm: 'md' }} style={{ minHeight: '100vh' }}>
            {/* Block all content if force update is required */}
            {showUpdateModal && versionCheck?.forceUpdate ? (
                <Container size="lg">
                    <Stack gap="md" align="center" justify="center" style={{ minHeight: '80vh' }}>
                        <Title order={2} ta="center">Update Required</Title>
                        <Text ta="center" c="dimmed">
                            {versionCheck.message || 'Please update to continue using the app.'}
                        </Text>
                        <Button
                            component="a"
                            href={versionCheck.updateUrl || 'https://kalimatai.com/download'}
                            target="_blank"
                            size="lg"
                            leftSection={<IconWand size={20} />}
                        >
                            Download Update
                        </Button>
                    </Stack>
                </Container>
            ) : (
                <>
                    <Container size="lg">
                        <Box pt={isAndroid() ? 'max(env(safe-area-inset-top, 0px), 32px)' : '0'}>
                        <Stack gap="xs" mb="xl">
                            <Group justify="space-between">
                                <Group>
                                    <Burger opened={drawerOpened} onClick={toggleDrawer} hiddenFrom="sm" size="sm" />
                                    <Title order={1} style={{ fontSize: rem(36) }} className="app-title">
                                        Kalimat AI
                                    </Title>
                                </Group>
                                <Group><NotificationBell /><LanguageSelector /><ThemeToggle /></Group>
                            </Group>
                            <Group justify="space-between" align="center">
                                <Group gap="xs" style={{ flex: 1, flexWrap: 'wrap' }}>
                                    <Text>{t('app.welcomeUser', { name: user?.name })}</Text>
                                    <AIModelSelector inline={true} />
                                    {callsRemaining && (
                                        <Text size="sm" c="dimmed">
                                            {(user?.plan || 'free') === 'premium'
                                                ? callsRemaining
                                                : callsRemaining}
                                        </Text>
                                    )}
                                </Group>
                                <Button variant="subtle" onClick={handleLogout} size="sm">{t('app.actions.logout')}</Button>
                            </Group>
                        </Stack>
                        </Box>

                <Drawer opened={drawerOpened} onClose={closeDrawer} title="Menu" padding="md" size="xs">
                    <Stack gap="xs">
                        <NavLink 
                            label={t('app.tabs.textCorrection')} 
                            leftSection={<ThemeIcon variant="light" size="sm"><IconWand style={{ width: '70%', height: '70%' }} /></ThemeIcon>} 
                            active={activeTab === 'text-correction'} 
                            onClick={() => { handleTabChange('text-correction'); closeDrawer(); }} 
                        />
                        <NavLink
                            label={t('app.tabs.history')}
                            leftSection={<ThemeIcon variant="light" size="sm"><IconHistory style={{ width: '70%', height: '70%' }} /></ThemeIcon>}
                            active={activeTab === 'history'}
                            onClick={() => { handleTabChange('history'); closeDrawer(); }}
                        />
                        {isAndroid() && (
                            <NavLink
                                label={t('app.tabs.clipboard')}
                                leftSection={<ThemeIcon variant="light" size="sm"><IconClipboard style={{ width: '70%', height: '70%' }} /></ThemeIcon>}
                                active={activeTab === 'clipboard'}
                                onClick={() => { handleTabChange('clipboard'); closeDrawer(); }}
                            />
                        )}
                        <NavLink
                            label={t('app.tabs.account')}
                            leftSection={<ThemeIcon variant="light" size="sm"><IconUser style={{ width: '70%', height: '70%' }} /></ThemeIcon>} 
                            active={activeTab === 'account'} 
                            onClick={() => { handleTabChange('account'); closeDrawer(); }} 
                        />
                        {user?.is_admin && (
                            <NavLink 
                                label={t('app.tabs.admin')} 
                                leftSection={<ThemeIcon variant="light" size="sm"><IconShield style={{ width: '70%', height: '70%' }} /></ThemeIcon>} 
                                active={activeTab === 'admin'} 
                                onClick={() => { handleTabChange('admin'); closeDrawer(); }} 
                            />
                        )}
                        <NavLink 
                            label={t('app.tabs.settings')} 
                            leftSection={<ThemeIcon variant="light" size="sm"><IconSettings style={{ width: '70%', height: '70%' }} /></ThemeIcon>} 
                            active={activeTab === 'settings'} 
                            onClick={() => { handleTabChange('settings'); closeDrawer(); }} 
                        />
                        <NavLink 
                            label={t('app.tabs.about')} 
                            leftSection={<ThemeIcon variant="light" size="sm"><IconInfoCircle style={{ width: '70%', height: '70%' }} /></ThemeIcon>} 
                            active={activeTab === 'about'} 
                            onClick={() => { handleTabChange('about'); closeDrawer(); }} 
                        />
                        <NavLink 
                            label={t('app.tabs.docs')} 
                            leftSection={<ThemeIcon variant="light" size="sm"><IconInfoCircle style={{ width: '70%', height: '70%' }} /></ThemeIcon>} 
                            active={activeTab === 'docs'} 
                            onClick={() => { handleTabChange('docs'); closeDrawer(); }} 
                        />
                    </Stack>
                </Drawer>

                <Tabs value={activeTab} onChange={handleTabChange}>
                    <Tabs.List grow visibleFrom="sm">
                        <Tabs.Tab
                            value="text-correction"
                            leftSection={<ThemeIcon variant="light" size="sm"><IconWand style={{ width: '70%', height: '70%' }} /></ThemeIcon>}
                        >
                            {t('app.tabs.textCorrection')}
                        </Tabs.Tab>
                        <Tabs.Tab
                            value="history"
                            leftSection={<ThemeIcon variant="light" size="sm"><IconHistory style={{ width: '70%', height: '70%' }} /></ThemeIcon>}
                        >
                            {t('app.tabs.history')}
                        </Tabs.Tab>
                        {isAndroid() && (
                            <Tabs.Tab
                                value="clipboard"
                                leftSection={<ThemeIcon variant="light" size="sm"><IconClipboard style={{ width: '70%', height: '70%' }} /></ThemeIcon>}
                            >
                                {t('app.tabs.clipboard')}
                            </Tabs.Tab>
                        )}
                        <Tabs.Tab
                            value="account"
                            leftSection={<ThemeIcon variant="light" size="sm"><IconUser style={{ width: '70%', height: '70%' }} /></ThemeIcon>}
                        >
                            {t('app.tabs.account')}
                        </Tabs.Tab>
                        {user?.is_admin ? (
                            <Tabs.Tab
                                value="admin"
                                leftSection={<ThemeIcon variant="light" size="sm"><IconShield style={{ width: '70%', height: '70%' }} /></ThemeIcon>}
                            >
                                {t('app.tabs.admin')}
                            </Tabs.Tab>
                        ) : null}
                        <Tabs.Tab
                            value="settings"
                            leftSection={<ThemeIcon variant="light" size="sm"><IconSettings style={{ width: '70%', height: '70%' }} /></ThemeIcon>}
                        >
                            {t('app.tabs.settings')}
                        </Tabs.Tab>
                        <Tabs.Tab
                            value="about"
                            leftSection={<ThemeIcon variant="light" size="sm"><IconInfoCircle style={{ width: '70%', height: '70%' }} /></ThemeIcon>}
                        >
                            {t('app.tabs.about')}
                        </Tabs.Tab>
                        <Tabs.Tab
                            value="docs"
                            leftSection={<ThemeIcon variant="light" size="sm"><IconInfoCircle style={{ width: '70%', height: '70%' }} /></ThemeIcon>}
                        >
                            {t('app.tabs.docs')}
                        </Tabs.Tab>
                    </Tabs.List>

                    {/* Main Content Panels */}
                    <Tabs.Panel value="text-correction" pt="md">
                        <TextCorrection
                            onAddToHistory={addToHistory}
                        />
                    </Tabs.Panel>

                    <Tabs.Panel value="history" pt="md">
                        <History
                            settings={settings || undefined}
                            user={user}
                            updateCallsRemaining={updateCallsRemaining}
                        />
                    </Tabs.Panel>

                    {isAndroid() && (
                        <Tabs.Panel value="clipboard" pt="md">
                            <Suspense fallback={null}>
                                <ClipboardHistory />
                            </Suspense>
                        </Tabs.Panel>
                    )}

                    <Tabs.Panel value="account" pt="md">
                        <AccountSettings user={user} />
                    </Tabs.Panel>

                    {user?.is_admin ? (
                        <Tabs.Panel value="admin" pt="md">
                            <AdminDashboard />
                        </Tabs.Panel>
                    ) : null}

                    <Tabs.Panel value="settings" pt="md">
                        <SettingsTabs onUpdateSettings={updateSettings} />
                    </Tabs.Panel>

                    <Tabs.Panel value="about" pt="md">
                        <About />
                    </Tabs.Panel>

                    <Tabs.Panel value="privacy" pt="md">
                        <Privacy />
                    </Tabs.Panel>

                    <Tabs.Panel value="docs" pt="md">
                        <Docs />
                    </Tabs.Panel>
                </Tabs>
            </Container>

            {/* Version Update Modal - only show for non-force updates */}
            {showUpdateModal && !versionCheck?.forceUpdate && (
                <Modal
                    opened={showUpdateModal}
                    onClose={() => setShowUpdateModal(false)}
                    title={
                        <Group>
                            <IconAlertTriangle size={24} color="orange" />
                            <Title order={4}>Update Required</Title>
                        </Group>
                    }
                    centered
                    closeOnClickOutside
                    closeOnEscape
                    withCloseButton
                    zIndex={10000}
                    overlayProps={{
                        backgroundOpacity: 0.7,
                        blur: 3
                    }}
                >
                    <Stack gap="md">
                        <Paper p="md" withBorder>
                            <Text fw={500} mb="xs">Your version is outdated</Text>
                            <Text size="sm" c="dimmed">
                                {versionCheck?.message || 'Please update to continue using the app.'}
                            </Text>
                        </Paper>

                        {versionCheck && (
                            <Group justify="space-between">
                                <div>
                                    <Text size="xs" c="dimmed">Current Version</Text>
                                    <Text fw={600}>1.0.0</Text>
                                </div>
                                <div>
                                    <Text size="xs" c="dimmed">Latest Version</Text>
                                    <Text fw={600}>{versionCheck.latestVersion}</Text>
                                </div>
                            </Group>
                        )}

                        <Button
                            component="a"
                            href={versionCheck?.updateUrl || 'https://kalimatai.com/download'}
                            target="_blank"
                            fullWidth
                            leftSection={<IconWand size={16} />}
                        >
                            Download Update
                        </Button>

                        <Button variant="subtle" onClick={() => setShowUpdateModal(false)} fullWidth>
                            Remind Me Later
                        </Button>
                    </Stack>
                </Modal>
            )}

            {/* Onboarding Modal */}
            <Onboarding 
                opened={showOnboarding} 
                onClose={() => setShowOnboarding(false)}
                onComplete={() => {
                    // Save the current version as the last seen onboarding version
                    localStorage.setItem('lastSeenOnboardingVersion', appVersion);
                    // Also save to backend if user is logged in
                    if (user) {
                        apiService.updateSettings({ lastSeenOnboardingVersion: appVersion }).catch(err => {
                            console.error('Failed to save onboarding version:', err);
                        });
                    }
                }}
            />
            </>
            )}
        </Box>

    );
}

function ModalSwitch() {
    const location = useLocation();
    const background = location.state?.background;

    return (
        <Routes location={background || location}>
            {/* Authentication routes */}
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/activate-account/:token" element={<ActivateAccount />} />
            <Route path="/auth/callback" element={<AuthCallback />} />

            {/* SEO pages - render directly without tabs */}
            <Route path="/blog" element={<SeoPageWrapper><Blog /></SeoPageWrapper>} />
            <Route path="/blog/:slug" element={<SeoPageWrapper><BlogPostPage /></SeoPageWrapper>} />
            <Route path="/privacy" element={<SeoPageWrapper><Privacy /></SeoPageWrapper>} />
            <Route path="/terms" element={<SeoPageWrapper><Terms /></SeoPageWrapper>} />
            <Route path="/grammar-checker" element={<SeoPageWrapper><GrammarCheckerPage /></SeoPageWrapper>} />
            <Route path="/translation" element={<SeoPageWrapper><TranslationAssistantPage /></SeoPageWrapper>} />
            <Route path="/floating-ai" element={<SeoPageWrapper><FloatingAIAssistantPage /></SeoPageWrapper>} />
            <Route path="/download" element={<SeoPageWrapper><DownloadPage /></SeoPageWrapper>} />

            {/* Main app route - everything else */}
            <Route path="/" element={<AppContent />} />
            <Route path="/:tab" element={<AppContent />} />
            <Route path="/:tab/:subtab" element={<AppContent />} />
        </Routes>
    );
}

// Add some global styles for mobile
const globalStyles = `
  .app-container.mobile {
    padding-bottom: 80px; /* Add padding for the floating button */
  }
  
  /* Improve touch targets on mobile */
  @media (max-width: 768px) {
    button, [role="button"], .mantine-Button-root {
      min-height: 44px !important; /* Minimum touch target size */
    }
    
    input, textarea, select {
      font-size: 16px !important; /* Prevent zoom on focus in iOS */
    }
    
    .app-title {
      font-size: 24px !important;
    }
  }
`;

// Add global styles for mobile
const styleElement = document.createElement('style');
styleElement.textContent = globalStyles;
document.head.appendChild(styleElement);

export default function App() {
    return (
        <BrowserRouter>
            <Suspense fallback={null}>
                {!isWeb() && isMobile() && <MobileAccessibilityButton />}
                {!isWeb() && isAndroid() && <AndroidAccessibilityOnboarding />}
            </Suspense>
            <div className={`app-container ${isMobile() ? 'mobile' : ''}`}>
                <ModalSwitch />
            </div>
        </BrowserRouter>
    );
}
